import React, {
	memo, useCallback, useEffect, useRef, useState,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ClipLoader from 'react-spinners/ClipLoader';
import {BsPauseFill} from 'react-icons/bs';
import {MdFavorite, MdShoppingCart} from 'react-icons/md';
import {BiDownload} from 'react-icons/bi';
import styled from 'styled-components';
import ReactWaves from '@dschoon/react-waves';
import {usePlayer} from './context/playerContext';
import {getTimeString} from '../../utils/date-utils';
import {getImagePath} from '../../utils/image-utils';
import PlayerProgress from './components/PlayerProgress';
import PlayerVolume from './components/PlayerVolume';
import useBasketButton from '../catalog/hooks/useBasketButton';
import {resetPageNav} from '../../store/reducers/catalogSlice';
import {useCatalog} from '../catalog/context/catalogContext';
import { useDropdown } from "../../hooks/useDropdown";
import WavePlaceholder from '../../components/WavePlaceholder';
import prevIcon from '../../img/sprite/player-prev.svg';
import nextIcon from '../../img/sprite/player-next.svg';
import playIcon from '../../img/sprite/player-play.svg';
import favoriteIcon from '../../img/sprite/player-favorite.svg';
import downloadIcon from '../../img/sprite/player-download.svg';
import cartIcon from '../../img/sprite/player-cart.svg';
import menuDotsIcon from '../../img/sprite/player-menu-dots.svg';


const StyledControl = styled.button`
  max-width: 24px;
  padding: 0;
`;

const AudioPlayerBottom = () => {
	const dispatch = useDispatch();
	const menu = useDropdown();
	const player = useRef();
	const {isAuth} = useSelector((state) => state.user);
	const [isFavorite, setIsFavorite] = useState(false);
	const {currentTrack, playerList} = useSelector((state) => state.player);
	const {favorite, loadingTrackId} = useSelector((state) => state.userList);
	const [volume, setVolume] = useState(1);
	const {setAlbum, setType} = useCatalog();
	const [volumeOpen, setVolumeOpen] = useState(false);

	const {
		loading,
		setLoading,
		progress,
		progress2,
		setProgress,
		setProgress2,
		position,
		setPosition,
		playing,
		setPlaying,
		favoriteHandler,
		nextTrack,
		prevTrack,
		src,
		loadTrack,
		play,
		togglePlayPause,
		arrows,
		setArrows,
	} = usePlayer();

	const [addTrackToBasket] = useBasketButton(isAuth, currentTrack);

	const onPosChange = useCallback((pos, wavesurfer) => {
		const duration = wavesurfer.getDuration();

		if (
			!Number.isFinite(pos)
			|| !Number.isFinite(duration)
			|| duration <= 0
		) {
			return;
		}

		const percent = Math.min(Math.max((pos / duration) * 100, 0), 100);
		setProgress(percent);
		setPosition(pos);
		localStorage.setItem('progress', percent.toString());
		localStorage.setItem('position', pos.toString());
	}, [setPosition, setProgress]);

	const volumeHandler = (value) => {
		const v = parseFloat(value);

		setVolume(v);

		localStorage.setItem('volume', v.toString());

		if (player.current) {
			player.current.setVolume(v);
		}
	};

	const onReady = useCallback(({ wavesurfer }) => {
		setLoading(false);
		setPlaying(true);
		wavesurfer.play();
		player.current = wavesurfer;

		const saved = parseFloat(localStorage.getItem('volume'));
		const v = !isNaN(saved) ? saved : 1;

		wavesurfer.setVolume(v);
		setVolume(v);
	}, [setLoading, setPlaying]);

	const nextTrackHandler = useCallback(() => {
		if (player.current) {
			try {
				player.current.pause();
			} catch (error) {
				player.current.backend.setState('paused');
			}
			nextTrack();
		}
	}, [nextTrack]);

	const setAlbumHandler = () => {
		if (currentTrack.albumInfo && currentTrack.albumInfo.CODE) {
			setAlbum(currentTrack.albumInfo.CODE);
			setType('albums');
			dispatch(resetPageNav());
		}
	};

	useEffect(() => {
		if (player.current && player.current.isReady) {
			setLoading(false);
		}
	}, [player, setLoading]);

	useEffect(() => {
		if (src) {
			setLoading(true);
		}
	}, [setLoading, src]);

	useEffect(() => {
		if (player.current && player.current.isReady) {
			const time = progress2 / 100;
			if (Number.isFinite(time)) {
				player.current.seekTo(Math.min(Math.max(time, 0), 1));
			}
		}
	}, [progress2]);

	useEffect(() => {
		if (!currentTrack.id) {
			const track = localStorage.getItem('track');
			const progress = localStorage.getItem('progress');
			const position = localStorage.getItem('position');
			if (track) {
				loadTrack(JSON.parse(track));
				setProgress(+progress);
				setProgress2(+progress);
				setPosition(+position);
				setVolume(0);
			}
		} else if (!!isAuth) {
			localStorage.setItem('track', JSON.stringify(currentTrack));
		} else {
			localStorage.removeItem('track');
		}
	}, [currentTrack, isAuth, loadTrack, setPosition, setProgress, setProgress2]);

	useEffect(() => {
		if (favorite && !!favorite.length) {
			setIsFavorite(favorite.map((f) => f.id).includes(currentTrack.id));
		}
	}, [favorite, currentTrack]);

	useEffect(() => {
		const trackIndex = playerList.findIndex((track) => track.id === currentTrack.id);
		// curArrows.next = playerList[trackIndex + 1] && playerList[trackIndex + 1].src;
		const prev = playerList[trackIndex - 1] && playerList[trackIndex - 1].src;
		setArrows({
			prev,
			next: !!playerList.length,
		});
	}, [playerList, currentTrack, setArrows]);

	useEffect(() => {
		window.__audioPlayer = {
			seekTo: (percent) => {
				if (player.current && player.current.isReady) {
					const time = percent / 100;
					if (Number.isFinite(time)) {
						player.current.seekTo(Math.min(Math.max(time, 0), 1));
					}
				}
			}
		};
		
		return () => {
			delete window.__audioPlayer;
		};
	}, []);

	useEffect(() => {
		if (progress >= 100) {
			nextTrack();
		}
	}, [nextTrack, progress]);

	const tagsList = currentTrack.tags
		? Object.values(currentTrack.tags).map((tag) => <li key={tag}>{tag}</li>)
		: null;

	const cover = getImagePath(currentTrack.albumPoster);

	return (
		<div className="player">
			<div
				className="player__progress-bar"
				style={{width: `${Math.min(Math.max(src ? progress : 0, 0), 100)}%`}}
			/>
			<div className="container container--large">
				<div className="player__wrapper d-flex align-items-center">
					<div className="player__controls d-flex align-items-center">
						<StyledControl
							onClick={prevTrack}
							className={`player__control player__control-track js-page-player-track ${src && arrows.prev ? '' : 'is-disabled'}`}
							type="button"
							data-direction="prev"
						>
							<img src={prevIcon} alt="" width={24} height={24}/>
						</StyledControl>
						<div className="player__control player__control-play">
							<PlayerProgress progress={src ? progress : 0}>
								<button
									onClick={(
										!!src && (togglePlayPause)
									)}
									className={`player__control-play-button js-page-player-play ${playing && play ? 'playing' : ''}`}
									type="button"
								>
									{
										loading
											? (
												<div style={{padding: '4px 0 0 0'}}>
													<ClipLoader color="#10DB92" size={20}/>
												</div>
											)
											: (
												playing && play
													? <BsPauseFill size={24} color="#10DB92"/>
													: <img src={playIcon} alt="" width={24} height={24}/>
											)
									}
								</button>
							</PlayerProgress>
						</div>
						<StyledControl
							onClick={nextTrackHandler}
							className={`player__control player__control-track js-page-player-track ${src && arrows.next ? '' : 'is-disabled'}`}
							type="button"
							data-direction="next"
						>
							<img src={nextIcon} alt="" width={24} height={24}/>
						</StyledControl>
					</div>
					<div className="player__song d-flex align-items-center">
						<figure className="player__poster page__image">
							<img
								src={cover}
								alt={currentTrack.name || ''}
								onClick={setAlbumHandler}
							/>
						</figure>
						<div className="player__song-info">
							<p className="player__song-name">
								<output className="js-page-player-song-name">
									{
										currentTrack.name
											? <span>{currentTrack.name}</span>
											: <span>Трек не выбран</span>
									}
								</output>
							</p>
							{!!tagsList && (
								<ul className="player__song-tags">
									{tagsList}
								</ul>
							)}
						</div>
					</div>
					<div className="player__duration d-flex align-items-center">
						<p className="player__duration-current js-page-player-song-duration-current">
							{src && position ? getTimeString(position.toFixed()) : '00:00'}
						</p>
						<div className="player__oscillogram">
							{src ? (
								<ReactWaves
									audioFile={currentTrack.src}
									className="react-waves"
									options={{
										barHeight: 2,
										barWidth: 6,
										barGap: 1,
										cursorWidth: 0,
										height: 40,
										hideScrollbar: true,
										progressColor: '#fff',
										responsive: true,
										waveColor: '#079A66',
										removeMediaElementOnDestroy: true,
									}}
									volume={volume}
									zoom={1}
									playing={playing && play}
									onPosChange={onPosChange}
									onReady={onReady}
									pos={position}
								/>
							) : (
								<WavePlaceholder
									barHeight={2}
									barWidth={6}
									barGap={1}
									barColor="#079A66"
									opacity={0.85}
								/>
							)}
						</div>
						<p className="player__duration-total js-page-player-song-duration-total">
							{src ? getTimeString(currentTrack.length) : '00:00'}
						</p>
					</div>
					<ul className="player__interaction d-flex align-items-center">
						<li className={`player__interaction-item player__interaction-item--volume js-dropdown ${volumeOpen ? 'is-active' : ''}`}>
							<PlayerVolume setVolume={volumeHandler} volume={volume} isActive={volumeOpen} toggle={() => setVolumeOpen((prev) => !prev)}/>
						</li>
						<li className="player__interaction-item player__interaction-item--favorite">
							{
								src
									? (
										<a
											className={`player__interaction-button waves-effect waves-button ${isFavorite ? 'is-active' : ''}`}
											href="#"
											onClick={() => favoriteHandler(currentTrack, isFavorite)}
										>
											{
												currentTrack.id !== loadingTrackId
													? <img src={favoriteIcon} alt="" width={24} height={24}/>
													: <ClipLoader color="#FFF" size={18}/>
											}
										</a>
									)
									: (
										<a className="player__interaction-button waves-button is-disabled">
											<img src={favoriteIcon} alt="" width={24} height={24}/>
										</a>
									)
							}
						</li>
						<li className="player__interaction-item player__interaction-item--download">
							{
								src
									? (
										<a
											className="player__interaction-button waves-effect waves-button"
											href={currentTrack.src}
											download={`${currentTrack.code} ${currentTrack.name}`}
										>
											<img src={downloadIcon} alt="" width={24} height={24}/>
										</a>
									)
									: (
										<a className="player__interaction-button waves-button is-disabled">
											<img src={downloadIcon} alt="" width={24} height={24}/>
										</a>
									)
							}
						</li>
						<li className="player__interaction-item player__interaction-item--cart">
							{
								src
									? (
										<a
											onClick={addTrackToBasket}
											className="player__interaction-button waves-effect waves-button"
										>
											<img src={cartIcon} alt="" width={24} height={24}/>
										</a>
									)
									: (
										<a className="player__interaction-button waves-button is-disabled">
											<img src={cartIcon} alt="" width={24} height={24}/>
										</a>
									)
							}
						</li>
						<li className="player__interaction-item player__interaction-item--menu js-dropdown" ref={menu.ref}>
							{src ? (
								<>
									<a
										className="player__interaction-button waves-effect waves-button js-dropdown-button"
										href="#"
										onClick={(e) => {
											e.preventDefault();
											menu.toggle();
										}}
									>
										<img src={menuDotsIcon} alt="" width={24} height={24}/>
									</a>
									<div
										className={`player__interaction-content player__menu js-dropdown-content ${
											menu.open ? 'is-active' : ''
										}`}
									>
										<ul className="player__menu-list">
											<ul className="player__menu-icons">
												<li className="player__interaction-item player__interaction-item--favorite">
													<a
														className={`player__interaction-button waves-effect waves-button ${isFavorite ? 'is-active' : ''}`}
														href="#"
														onClick={() => favoriteHandler(currentTrack, isFavorite)}
													>
														{
															currentTrack.id !== loadingTrackId
																? <MdFavorite size={24}/>
																: <ClipLoader color="#FFF" size={18}/>
														}
													</a>
												</li>
												<li className="player__interaction-item player__interaction-item--download">
													<a
														className="player__interaction-button waves-effect waves-button"
														href={currentTrack.src}
														download={`${currentTrack.code} ${currentTrack.name}`}
													>
														<BiDownload size={24} color="#fff"/>
													</a>
												</li>
												<li className="player__interaction-item player__interaction-item--cart">
													<a
														onClick={addTrackToBasket}
														className="player__interaction-button waves-effect waves-button"
													>
														<MdShoppingCart size={24} color="#fff"/>
													</a>
												</li>
											</ul>
											<li>
												<a className="d-inline-block" onClick={setAlbumHandler}>
													Перейти в альбом
												</a>
											</li>
										</ul>
									</div>
								</>
							) : (
								<a className="player__interaction-button waves-button is-disabled">
									<img src={menuDotsIcon} alt="" width={24} height={24}/>
								</a>
							)}
						</li>
					</ul>
				</div>
			</div>
		</div>
	);
};

export default memo(AudioPlayerBottom);
